import { useEffect, useId, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/Button/Button';
import { getCopyTargetCourseOptions } from '@/features/content/componentActions/courseOptionUtils';
import { createEditorState } from '@/features/content/editor/editorNormalizer';
import type { RawComponent } from '@/features/content/editor/editorTypes';
import { useCourse } from '@/features/courses/useCourse';
import { useCourses } from '@/features/courses/useCourses';

import { groupUnitsIntoSections } from './groupUnitsIntoSections';
import type {
  ContentSection,
  CopySectionPosition,
  SectionInsertPlacement,
} from './sectionTypes';
import { useCopySection } from './useCopySection';
import styles from './CopySectionDialog.module.css';

export interface CopySectionSuccess {
  message: string;
  savedComponent: RawComponent;
  targetComponentId: string;
  targetCourseId: string;
}

interface CopySectionDialogProps {
  currentComponent: RawComponent;
  /**
   * The chapter open on the page has changes not yet saved. A copy into it
   * goes to the server's copy, without them, and the page's next Save would
   * write them over the copy: so a copy into it waits until they are saved
   * or cancelled.
   */
  currentComponentHasUnsavedChanges?: boolean;
  currentComponentId: string;
  currentComponentTitle: string;
  currentCourseId: string;
  currentCourseTitle: string;
  onOpenChange(open: boolean): void;
  onSuccess(result: CopySectionSuccess): void | Promise<void>;
  open: boolean;
  sections: ContentSection[];
}

interface TargetComponentOption {
  id: string;
  rawComponent: RawComponent;
  title: string;
}

const UNSAVED_TARGET_MESSAGE =
  'Save or cancel your changes to this chapter first, then copy into it.';

function getErrorMessage(error: unknown) {
  return error instanceof Error && error.message
    ? error.message
    : 'Unable to copy this section right now.';
}

export function CopySectionDialog({
  currentComponent,
  currentComponentHasUnsavedChanges = false,
  currentComponentId,
  currentComponentTitle,
  currentCourseId,
  currentCourseTitle,
  onOpenChange,
  onSuccess,
  open,
  sections,
}: CopySectionDialogProps) {
  const courseSelectId = useId();
  const componentSelectId = useId();
  const sectionSelectId = useId();
  const coursesQuery = useCourses();
  const copySection = useCopySection();
  const [selectedCourseId, setSelectedCourseId] = useState(currentCourseId);
  const [selectedComponentId, setSelectedComponentId] =
    useState(currentComponentId);
  const [placement, setPlacement] = useState<SectionInsertPlacement>('after');
  const [selectedSectionId, setSelectedSectionId] = useState('');
  const selectedCourseQuery = useCourse(selectedCourseId || undefined);
  const courses = coursesQuery.data ?? [];
  const userCourseIds =
    coursesQuery.data?.userCourseIds && coursesQuery.data.userCourseIds.length > 0
      ? coursesQuery.data.userCourseIds
      : courses.map((course) => course.id);
  const courseOptions = useMemo(
    () =>
      getCopyTargetCourseOptions({
        courses,
        currentCourseId,
        currentCourseTitle,
        userCourseIds,
      }),
    [courses, currentCourseId, currentCourseTitle, userCourseIds],
  );
  const selectedCourse = courseOptions.find(
    (option) => option.id === selectedCourseId,
  );
  const targetComponentOptions = useMemo<TargetComponentOption[]>(() => {
    const options =
      selectedCourseQuery.data?.components
        .flatMap((component) => {
          if (component.kind !== 'component' || !component.rawComponent) {
            return [];
          }

          return [
            {
              id: component.componentId ?? component.id,
              rawComponent: component.rawComponent as unknown as RawComponent,
              title: component.title || 'Untitled chapter',
            },
          ];
        }) ?? [];

    if (selectedCourseId !== currentCourseId) {
      return options;
    }

    if (options.some((option) => option.id === currentComponentId)) {
      return options;
    }

    return [
      {
        id: currentComponentId,
        rawComponent: currentComponent,
        title: currentComponentTitle || 'Untitled chapter',
      },
      ...options,
    ];
  }, [
    currentComponent,
    currentComponentId,
    currentComponentTitle,
    currentCourseId,
    selectedCourseId,
    selectedCourseQuery.data?.components,
  ]);
  const targetComponent = targetComponentOptions.find(
    (option) => option.id === selectedComponentId,
  );
  const targetSections = useMemo(() => {
    if (!targetComponent) {
      return [];
    }

    return groupUnitsIntoSections(
      createEditorState(targetComponent.rawComponent).contentUnits,
    ).sections;
  }, [targetComponent]);
  const copyTitle =
    sections.length === 1
      ? `Copy "${sections[0]?.title ?? 'Untitled section'}"`
      : `Copy ${sections.length} sections`;
  const position: CopySectionPosition | null =
    targetSections.length === 0
      ? { placement: 'first' }
      : selectedSectionId
        ? { placement, sectionId: selectedSectionId }
        : null;

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedCourseId(currentCourseId);
    setSelectedComponentId(currentComponentId);
    setPlacement('after');
    setSelectedSectionId('');
  }, [currentComponentId, currentCourseId, open]);

  useEffect(() => {
    if (!open || targetComponentOptions.length === 0) {
      return;
    }

    if (
      !selectedComponentId ||
      !targetComponentOptions.some((option) => option.id === selectedComponentId)
    ) {
      setSelectedComponentId(targetComponentOptions[0]!.id);
    }
  }, [open, selectedComponentId, targetComponentOptions]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (targetSections.length === 0) {
      setSelectedSectionId('');
      return;
    }

    if (!targetSections.some((section) => section.id === selectedSectionId)) {
      setSelectedSectionId(targetSections[0]!.id);
    }
  }, [open, selectedSectionId, targetSections]);

  const isCopyIntoUnsavedChapter =
    currentComponentHasUnsavedChanges &&
    selectedCourseId === currentCourseId &&
    targetComponent?.id === currentComponentId;

  async function handleCopy() {
    if (!targetComponent || !selectedCourse || !position || isCopyIntoUnsavedChapter) {
      return;
    }

    const savedComponent = await copySection.mutateAsync({
      position,
      sections,
      targetComponent: targetComponent.rawComponent,
      targetComponentId: targetComponent.id,
      targetCourseId: selectedCourse.id,
    });

    await onSuccess({
      message: `Copied to ${targetComponent.title} in ${selectedCourse.label}`,
      savedComponent,
      targetComponentId: targetComponent.id,
      targetCourseId: selectedCourse.id,
    });
    onOpenChange(false);
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content}>
          <div className={styles.header}>
            <div>
              <Dialog.Title className={styles.title}>{copyTitle}</Dialog.Title>
              <Dialog.Description asChild>
                <p className={styles.description}>
                  Choose a course, chapter, and insertion point for the copied
                  section content.
                </p>
              </Dialog.Description>
            </div>
          </div>

          <div className={styles.body}>
            <label className={styles.field} htmlFor={courseSelectId}>
              <span className={styles.label}>Copy to course</span>
              <select
                className={styles.select}
                disabled={coursesQuery.isPending || copySection.isPending}
                id={courseSelectId}
                onChange={(event) => {
                  setSelectedCourseId(event.target.value);
                  setSelectedComponentId('');
                  setSelectedSectionId('');
                }}
                value={selectedCourseId}
              >
                {courseOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label} · {option.id}
                    {option.isCurrent ? ' (current)' : ''}
                  </option>
                ))}
              </select>
            </label>

            <label className={styles.field} htmlFor={componentSelectId}>
              <span className={styles.label}>Copy to chapter</span>
              <select
                className={styles.select}
                disabled={selectedCourseQuery.isPending || copySection.isPending}
                id={componentSelectId}
                onChange={(event) => {
                  setSelectedComponentId(event.target.value);
                  setSelectedSectionId('');
                }}
                value={selectedComponentId}
              >
                {selectedCourseQuery.isPending ? (
                  <option value="">Loading chapters…</option>
                ) : targetComponentOptions.length === 0 ? (
                  <option value="">No chapters available</option>
                ) : (
                  targetComponentOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.title || 'Untitled chapter'}
                    </option>
                  ))
                )}
              </select>
            </label>

            {targetComponent && targetSections.length === 0 ? (
              <p className={styles.helperText}>
                This target chapter has no numbered sections yet. The copied
                section will be added as the first section.
              </p>
            ) : null}

            {targetSections.length > 0 ? (
              <fieldset
                className={styles.positionGroup}
                disabled={copySection.isPending}
              >
                <legend className={styles.label}>Position</legend>
                <label className={styles.radioRow}>
                  <input
                    checked={placement === 'before'}
                    name="copy-section-position"
                    onChange={() => setPlacement('before')}
                    type="radio"
                  />
                  <span>Before</span>
                  <select
                    aria-label="Section to copy before or after"
                    className={styles.inlineSelect}
                    id={sectionSelectId}
                    onChange={(event) => setSelectedSectionId(event.target.value)}
                    value={selectedSectionId}
                  >
                    {targetSections.map((section) => (
                      <option key={section.id} value={section.id}>
                        {section.title}
                      </option>
                    ))}
                  </select>
                </label>
                <label className={styles.radioRow}>
                  <input
                    checked={placement === 'after'}
                    name="copy-section-position"
                    onChange={() => setPlacement('after')}
                    type="radio"
                  />
                  <span>After</span>
                </label>
              </fieldset>
            ) : null}

            {isCopyIntoUnsavedChapter ? (
              <p className={styles.helperText} role="status">
                {UNSAVED_TARGET_MESSAGE}
              </p>
            ) : null}

            {copySection.isError ? (
              <p aria-live="polite" className={styles.error} role="alert">
                {getErrorMessage(copySection.error)}
              </p>
            ) : null}
          </div>

          <div className={styles.footer}>
            <Dialog.Close asChild>
              <Button disabled={copySection.isPending} variant="secondary">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              disabled={
                !targetComponent ||
                !position ||
                sections.length === 0 ||
                isCopyIntoUnsavedChapter ||
                copySection.isPending
              }
              onClick={() => {
                void handleCopy();
              }}
            >
              {copySection.isPending ? 'Copying…' : 'Copy'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
